import React from 'react';
import { Group, Rect, Text } from 'react-konva';
import { useEditorStore } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';
import {
  getTemperatureLens,
  getPowerLens,
  getSpaceCapacityLens,
  getSpofLens,
  LensMetricResult,
} from '../../utils/lenses.logic';

export const LensLayer: React.FC = () => {
  const { racks, activeLens, telemetryData, whatIfActive, whatIfData, stageScale } = useEditorStore(
    useShallow(state => ({
      racks: state.racks,
      activeLens: state.activeLens,
      telemetryData: state.telemetryData,
      whatIfActive: state.whatIfActive,
      whatIfData: state.whatIfData,
      stageScale: state.stageScale,
    }))
  );

  if (activeLens === 'none') return null;

  const currentTelemetry = whatIfActive ? whatIfData : telemetryData;
  const showDetail = stageScale >= 0.45;

  return (
    <Group listening={false}>
      {Object.values(racks).map(rack => {
        const tele = currentTelemetry[rack.id];
        const w = (rack.widthM || 0.6) * 100;
        const h = (rack.depthM || 1.0) * 100;
        const rot = rack.rotation || 0;
        const isPillar = Boolean(rack.isBlocked || rack.rackType === 'pillar');
        const isFac = Boolean(rack.isFacility || ['crac', 'inrow_cooler', 'ups', 'pdu_frame'].includes(rack.rackType));

        let metric: LensMetricResult;
        if (activeLens === 'temperature') {
          metric = getTemperatureLens(tele?.temperature);
        } else if (activeLens === 'power') {
          const cap = tele?.powerCapacity ?? (rack.max_power_w ? rack.max_power_w / 1000 : 5.0);
          metric = getPowerLens(tele?.powerLoad ?? 0, cap);
        } else if (activeLens === 'space') {
          const capU = tele?.totalU ?? rack.uCount ?? 42;
          metric = getSpaceCapacityLens(tele?.usedU ?? 0, capU, isFac || isPillar);
        } else if (activeLens === 'spof') {
          metric = getSpofLens(tele?.hasSpof, tele?.spofReason);
        } else {
          metric = { fill: 'transparent', badge: '', status: '' };
        }

        // Suppress badge on pillars always; on facility units, suppress only in space capacity lens
        const hideBadge = isPillar || (activeLens === 'space' && isFac);

        // Pill badge dimensions — fits within the minimum dimension so it never clips at 0, 90, 180, 270 deg
        const minDim = Math.min(w, h);
        const badgeW = Math.max(32, minDim - 8);
        const badgeH = 15;

        return (
          <Group
            key={`lens-${rack.id}`}
            x={rack.x + w / 2}
            y={rack.y + h / 2}
            offsetX={w / 2}
            offsetY={h / 2}
            rotation={rot}
          >
            {/* Color overlay matching exact rack rotation and dimensions */}
            <Rect
              x={0}
              y={0}
              width={w}
              height={h}
              fill={isPillar ? 'rgba(75, 85, 99, 0.2)' : metric.fill}
              cornerRadius={4}
            />

            {/* Numeric telemetry badge — centered at rack center and counter-rotated to stay upright */}
            {showDetail && metric.badge && !hideBadge && (
              <Group
                x={w / 2}
                y={h / 2}
                offsetX={badgeW / 2}
                offsetY={badgeH / 2}
                rotation={-rot}
              >
                <Rect
                  x={0}
                  y={0}
                  width={badgeW}
                  height={badgeH}
                  fill="rgba(10, 15, 28, 0.85)"
                  stroke="rgba(255, 255, 255, 0.15)"
                  strokeWidth={0.8}
                  cornerRadius={3}
                />
                <Text
                  x={0}
                  y={2}
                  width={badgeW}
                  align="center"
                  text={metric.badge}
                  fontSize={Math.max(7.5, Math.min(10, minDim * 0.18))}
                  fontFamily="JetBrains Mono, monospace"
                  fontStyle="bold"
                  fill="#ffffff"
                />
              </Group>
            )}
          </Group>
        );
      })}
    </Group>
  );
};
